import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import DOMPurify from "dompurify";
import { PhotoLightbox } from "@/components/message-attachments";

const HTML_TAG_RE = /<\/?[a-zA-Z][\s\S]*?>/;

const ALLOWED_TAGS = [
  "img",
  "a",
  "br",
  "p",
  "span",
  "strong",
  "em",
  "b",
  "i",
  "u",
  "ul",
  "ol",
  "li",
  "blockquote",
  "code",
  "pre",
  "mark",
];

const ALLOWED_ATTR = ["src", "alt", "title", "width", "height", "href", "target", "rel", "class"];

const SAFE_URL_RE = /^(https?:|mailto:|tel:)/i;
const SAFE_IMG_SRC_RE = /^(https?:|data:image\/)/i;

let hookInstalled = false;
function ensureHookInstalled() {
  if (hookInstalled) return;
  hookInstalled = true;
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (!(node instanceof Element)) return;
    if (node.tagName === "A") {
      const href = node.getAttribute("href");
      if (href && !SAFE_URL_RE.test(href)) {
        node.removeAttribute("href");
      } else if (href) {
        node.setAttribute("target", "_blank");
        node.setAttribute("rel", "noopener noreferrer");
      }
    }
    if (node.tagName === "IMG") {
      const src = node.getAttribute("src");
      if (!src || !SAFE_IMG_SRC_RE.test(src)) {
        node.parentNode?.removeChild(node);
      }
    }
  });
}

export function containsHtml(text: string): boolean {
  return HTML_TAG_RE.test(text);
}

export function sanitizeMessageHtml(text: string): string {
  ensureHookInstalled();
  return DOMPurify.sanitize(text, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form", "input", "link", "meta"],
    FORBID_ATTR: ["onerror", "onclick", "onload", "onmouseover", "onfocus", "onblur", "onchange", "onsubmit"],
  });
}

// Task #2297 — Compute merged, non-overlapping match ranges for a string
// against a set of case-insensitive search words. Shared by both the
// plaintext and HTML rendering paths so multi-word searches highlight each
// occurrence independently and longer words don't double-wrap.
type Range = { start: number; end: number };
function computeMatchRanges(text: string, words: string[]): Range[] {
  const filtered = words.filter((w) => w.length > 0);
  if (filtered.length === 0) return [];
  const lower = text.toLowerCase();
  const ranges: Range[] = [];
  for (const w of filtered) {
    let from = 0;
    while (true) {
      const idx = lower.indexOf(w, from);
      if (idx === -1) break;
      ranges.push({ start: idx, end: idx + w.length });
      from = idx + w.length;
    }
  }
  if (ranges.length === 0) return [];
  ranges.sort((a, b) => a.start - b.start);
  const merged: Range[] = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (last && r.start <= last.end) {
      last.end = Math.max(last.end, r.end);
    } else {
      merged.push({ ...r });
    }
  }
  return merged;
}

function renderPlaintextWithHighlights(text: string, words: string[]): ReactNode {
  const merged = computeMatchRanges(text, words);
  if (merged.length === 0) return text;
  const out: ReactNode[] = [];
  let cursor = 0;
  merged.forEach((r, i) => {
    if (cursor < r.start) out.push(text.slice(cursor, r.start));
    out.push(
      <mark key={i} className="rounded bg-yellow-300 px-0.5 font-semibold text-gray-900">
        {text.slice(r.start, r.end)}
      </mark>,
    );
    cursor = r.end;
  });
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}

// Walk the sanitized DOM, wrapping each match inside text nodes with a
// <mark>. We do this post-sanitization so the highlight wrapping cannot
// introduce XSS — the surrounding HTML has already been laundered by
// DOMPurify and we only ever insert plain text plus our own <mark> tags.
function highlightSanitizedHtml(html: string, words: string[]): string {
  const filtered = words.filter((w) => w.length > 0);
  if (filtered.length === 0) return html;
  if (typeof window === "undefined" || typeof document === "undefined") return html;
  const container = document.createElement("div");
  container.innerHTML = html;
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.textContent || !node.textContent.trim()) return NodeFilter.FILTER_REJECT;
      const parent = node.parentElement;
      // Don't re-wrap text that's already inside a <mark>.
      if (parent && parent.tagName === "MARK") return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const textNodes: Text[] = [];
  let n: Node | null = walker.nextNode();
  while (n) {
    textNodes.push(n as Text);
    n = walker.nextNode();
  }
  for (const textNode of textNodes) {
    const original = textNode.textContent ?? "";
    const merged = computeMatchRanges(original, filtered);
    if (merged.length === 0) continue;
    const frag = document.createDocumentFragment();
    let cursor = 0;
    for (const r of merged) {
      if (cursor < r.start) frag.appendChild(document.createTextNode(original.slice(cursor, r.start)));
      const mark = document.createElement("mark");
      mark.className = "rounded bg-yellow-300 px-0.5 font-semibold text-gray-900";
      mark.textContent = original.slice(r.start, r.end);
      frag.appendChild(mark);
      cursor = r.end;
    }
    if (cursor < original.length) frag.appendChild(document.createTextNode(original.slice(cursor)));
    textNode.parentNode?.replaceChild(frag, textNode);
  }
  return container.innerHTML;
}

// Compute the final HTML outside the component so that the value is stable
// and can be used as a useEffect dependency without causing infinite loops.
function buildFinalHtml(text: string, words: string[]): string {
  const clean = sanitizeMessageHtml(text);
  return words.length > 0 ? highlightSanitizedHtml(clean, words) : clean;
}

export function MessageBody({
  text,
  className,
  highlightWords,
}: {
  text: string;
  className?: string;
  /** Task #2297 — When non-empty, wrap occurrences of these (case-insensitive)
   * words in <mark> inside the rendered body so hosts can see which part of a
   * long message was matched by the inbox search. */
  highlightWords?: string[];
}) {
  const words = (highlightWords ?? []).filter((w) => w.length > 0);
  const isHtml = containsHtml(text);
  // Compute finalHtml unconditionally so it can be used as a stable effect dep.
  const finalHtml = isHtml ? buildFinalHtml(text, words) : "";

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [imageSrcs, setImageSrcs] = useState<string[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // After the HTML is painted, collect all <img> srcs and wire click handlers
  // so each thumbnail opens the lightbox at the right index.
  // Depend on finalHtml so we only re-run when the rendered content changes.
  useEffect(() => {
    if (!isHtml) return;
    const container = wrapperRef.current;
    if (!container) return;

    const imgs = Array.from(container.querySelectorAll<HTMLImageElement>("img"));
    const srcs = imgs.map((img) => img.src).filter(Boolean);
    // Use functional updater to avoid re-render when srcs haven't changed.
    setImageSrcs((prev) => {
      if (prev.length === srcs.length && prev.every((s, i) => s === srcs[i])) return prev;
      return srcs;
    });

    const cleanups = imgs.map((img, idx) => {
      const handler = () => setLightboxIndex(idx);
      img.addEventListener("click", handler);
      return () => img.removeEventListener("click", handler);
    });

    return () => {
      cleanups.forEach((fn) => fn());
    };
  }, [finalHtml, isHtml]);

  if (!isHtml) {
    return (
      <div className={`whitespace-pre-wrap ${className ?? ""}`.trim()}>
        {words.length > 0 ? renderPlaintextWithHighlights(text, words) : text}
      </div>
    );
  }

  return (
    <>
      <div
        ref={wrapperRef}
        className={`whitespace-pre-wrap [&_img]:h-28 [&_img]:w-28 [&_img]:object-cover [&_img]:rounded-md [&_img]:my-1 [&_img]:cursor-zoom-in [&_img]:border [&_img]:border-gray-200 [&_a]:underline ${className ?? ""}`.trim()}
        dangerouslySetInnerHTML={{ __html: finalHtml }}
      />
      {imageSrcs.length > 0 && (
        <PhotoLightbox
          images={imageSrcs}
          index={lightboxIndex}
          messageId="inline"
          onIndexChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
}
